import { useState, createContext, useMemo } from "react";
import { AlertColor } from "@mui/material/Alert";

export interface Notification {
  message: string;
  severity: AlertColor;
}

export interface NotifierContextT {
  isOpen: boolean;
  notification: Notification;
  showNotification: (notification: Notification) => void;
  hideNotification: () => void;
}

export const NotifierContext = createContext<NotifierContextT>({
  isOpen: false,
  notification: {
    message: "",
    severity: "success",
  },
  showNotification: () => undefined,
  hideNotification: () => undefined,
});

function NotifierProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [notification, setNotification] = useState<Notification>({
    message: "",
    severity: "success",
  });

  const hideNotification = () => setIsOpen(false);

  const showNotification = ({ message, severity }: { message: string; severity: AlertColor }) => {
    setIsOpen(false);
    setIsOpen(true);
    setNotification({ message, severity });
  };

  const contextValue = useMemo(
    () => ({
      isOpen,
      notification,
      showNotification: ({ message, severity }: Notification) => showNotification({ message, severity }),
      hideNotification: () => hideNotification(),
    }),
    [isOpen, notification, hideNotification, showNotification]
  );

  return <NotifierContext.Provider value={contextValue}>{children}</NotifierContext.Provider>;
}

export default NotifierProvider;
